import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { HELP_TOPICS, getHelpTopic } from '@emr/shared';

import { Panel } from '../components/primitives.tsx';

/**
 * The dedicated help page (`BACKLOG.md` B6). Sections render `@emr/shared`'s `HELP_TOPICS` in their
 * own declared order — the file's own header comment is explicit that this is a real-confusion
 * narrative order, not the feature list, per `claude-kit/rules/invariants.md`.
 *
 * **Public, deliberately not behind `RequireAuth`.** Unlike every other route in this app, this page
 * carries no PHI and calls no API — it is static prose describing the application itself, so a
 * person can read it (or follow a link a support message sent them, from `LoginPage.tsx`'s own
 * `HelpLink`) before they have ever signed in. `App.tsx` mounts it as a sibling of `/login`, outside
 * the authenticated shell, for exactly this reason.
 *
 * **Deep-linkable with a graceful stale-link fallback.** `/help/:topicId` scrolls to and focuses
 * that section on mount; `/help` alone (or an id that does not match any current topic — a renamed
 * or removed one) falls back to the top of the page rather than a 404 or a silent no-op, matching
 * `invariants.md`'s explicit requirement. Scroll/focus is handled manually in an effect rather than
 * relying on the browser's native anchor-scroll: this app uses `<BrowserRouter>` with real paths, so
 * a client-side navigation to `/help/your-access` is a `pushState`, not a full navigation, and a
 * `pushState` does not trigger the browser's own fragment-scroll behaviour the way following a
 * literal `#anchor` link with a full page load would.
 */
export function HelpPage() {
  const { t } = useTranslation('help');
  const { topicId } = useParams<{ topicId?: string }>();
  const topRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const target = topicId !== undefined ? document.getElementById(topicId) : null;
    const el = target ?? topRef.current;
    el?.scrollIntoView({ block: 'start' });
    el?.focus();
    // Every section heading and the page's own top heading carry `tabIndex={-1}` below specifically
    // so this focus call has something focusable to land on — a `<section>`/`<h1>` is not
    // focusable by default, and moving focus (not just scrolling) is what actually helps a
    // keyboard or screen-reader user land where the link claimed to send them.
  }, [topicId]);

  const staleLink = topicId !== undefined && getHelpTopic(topicId) === null;

  return (
    <div className="flex flex-col gap-6">
      <h1 ref={topRef} tabIndex={-1} className="m-0 text-xl font-semibold text-ink outline-none">
        {t('pageTitle')}
      </h1>

      {staleLink ? (
        <Panel>
          <p className="m-0 text-sm text-ink">{t('staleLink')}</p>
        </Panel>
      ) : null}

      <p className="m-0 max-w-prose text-sm text-ink-muted">{t('intro')}</p>

      {HELP_TOPICS.map((topic) => (
        <section
          key={topic.id}
          id={topic.id}
          tabIndex={-1}
          aria-labelledby={`${topic.id}-heading`}
          className="scroll-mt-4 rounded-panel border border-line bg-surface p-4 shadow-panel outline-none sm:p-6"
        >
          <h2 id={`${topic.id}-heading`} className="mt-0 mb-3 text-lg font-semibold text-ink">
            {t(`topics.${topic.id}.title`, { defaultValue: topic.title })}
          </h2>
          {topic.body.map((paragraph, index) => (
            // Index is a stable key here: `topic.body` is static, declared once in `topics.ts` and
            // never reordered at runtime. The English `paragraph` itself is only a `defaultValue`
            // fallback — `topics.ts` stays the canonical source `topicMatcher.ts`'s server-side
            // grounding matches against, untouched, and `help.json`'s `topics.<id>.body.<index>`
            // keys are the translated text actually shown, looked up by the same index rather than
            // storing the body as a JSON array (`locales/parity.test.ts`'s `flatten()` rejects
            // arrays — every leaf must be a string or a nested object).
            <p key={index} className="m-0 mt-2 text-sm text-ink first:mt-0">
              {t(`topics.${topic.id}.body.${String(index)}`, { defaultValue: paragraph })}
            </p>
          ))}
        </section>
      ))}
    </div>
  );
}
